/*!
 * devextreme-react
 * Version: 23.2.11
 * Build date: Mon Dec 16 2024
 *
 * Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-react
 */

var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import * as events from 'devextreme/events';
import { useState, useMemo, useCallback, useEffect, useRef, } from 'react';
import { TemplateWrapper } from './template-wrapper';
import { TemplateInstantiationModels, generateID } from './helpers';
import { DX_REMOVE_EVENT } from './component-base';
import { getOption as getConfigOption } from './config';
function normalizeProps(props) {
    if (getConfigOption('useLegacyTemplateEngine')) {
        var model = props.data;
        if (model && Object.prototype.hasOwnProperty.call(model, 'key')) {
            model.dxkey = model.key;
        }
        return model;
    }
    return props;
}
export var TemplateManager = function (_a) {
    var init = _a.init, onTemplatesRendered = _a.onTemplatesRendered;
    var _b = __read(useState({
        collection: new TemplateInstantiationModels(),
    }), 2), instantiationModels = _b[0], setInstantiationModels = _b[1];
    var _c = __read(useState(), 2), updateContext = _c[0], setUpdateContext = _c[1];
    var widgetId = useRef('');
    var templateFactories = useRef({});
    var subscribeOnRemoval = useCallback(function (container, onRemoved) {
        if (container.nodeType === Node.ELEMENT_NODE) {
            events.on(container, DX_REMOVE_EVENT, onRemoved);
        }
    }, []);
    var unsubscribeOnRemoval = useCallback(function (container, onRemoved) {
        if (container.nodeType === Node.ELEMENT_NODE) {
            events.off(container, DX_REMOVE_EVENT, onRemoved);
        }
    }, []);
    var unwrapElement = useCallback(function (element) { return (element.get ? element.get(0) : element); }, []);
    var createMapKey = useCallback(function (key1, key2) { return ({ key1: key1, key2: key2 }); }, []);
    var getRandomId = useCallback(function () { return "" + generateID() + generateID() + generateID(); }, []);
    var collection = instantiationModels.collection;
    var getRenderFunc = useCallback(function (templateKey) { return function (_a) {
        var data = _a.model, index = _a.index, container = _a.container, onRendered = _a.onRendered;
        var containerElement = unwrapElement(container);
        var key = createMapKey(data, containerElement);
        var onRemoved = function () {
            if (collection.get(key)) {
                collection.delete(key);
                setInstantiationModels({ collection: collection });
            }
        };
        var hostWidgetId = widgetId.current;
        collection.set(key, {
            templateKey: templateKey,
            index: index,
            componentKey: getRandomId(),
            onRendered: function () {
                unsubscribeOnRemoval(containerElement, onRemoved);
                if (hostWidgetId === widgetId.current) {
                    onRendered === null || onRendered === void 0 ? void 0 : onRendered();
                }
            },
            onRemoved: onRemoved,
        });
        setInstantiationModels({ collection: collection });
        return containerElement;
    }; }, [unsubscribeOnRemoval, createMapKey, collection]);
    useMemo(function () {
        function getTemplateFunction(template) {
            switch (template.type) {
                case 'children': return function () { return template.content; };
                case 'render': return function (props) {
                    normalizeProps(props);
                    return template.content(props.data, props.index);
                };
                case 'component': return function (props) {
                    props = normalizeProps(props);
                    return React.createElement.bind(null, template.content)(props);
                };
                default: return function () { return React.createElement(React.Fragment); };
            }
        }
        function createDXTemplates(templateOptions) {
            var factories = Object.entries(templateOptions)
                .reduce(function (res, _a) {
                var _b;
                var _c = __read(_a, 2), key = _c[0], template = _c[1];
                return (__assign(__assign({}, res), (_b = {}, _b[key] = getTemplateFunction(template), _b)));
            }, {});
            templateFactories.current = factories;
            var dxTemplates = Object.keys(factories)
                .reduce(function (templates, templateKey) {
                templates[templateKey] = { render: getRenderFunc(templateKey) };
                return templates;
            }, {});
            return dxTemplates;
        }
        function clearInstantiationModels() {
            widgetId.current = getRandomId();
            instantiationModels.collection.clear();
            setInstantiationModels(__assign({}, instantiationModels));
        }
        function updateTemplates(onUpdated) {
            setUpdateContext({ onUpdated: onUpdated });
        }
        init({ createDXTemplates: createDXTemplates, clearInstantiationModels: clearInstantiationModels, updateTemplates: updateTemplates });
    }, [init, getRenderFunc]);
    useEffect(function () {
        if (updateContext) {
            updateContext.onUpdated();
        }
        onTemplatesRendered();
    }, [updateContext, onTemplatesRendered]);
    if (instantiationModels.collection.empty) {
        return null;
    }
    return (React.createElement(React.Fragment, null, Array.from(instantiationModels.collection).map(function (_a) {
        var _b = __read(_a, 2), _c = _b[0], data = _c.key1, container = _c.key2, _d = _b[1], index = _d.index, templateKey = _d.templateKey, componentKey = _d.componentKey, onRendered = _d.onRendered, onRemoved = _d.onRemoved;
        subscribeOnRemoval(container, onRemoved);
        var factory = templateFactories.current[templateKey];
        if (factory) {
            return React.createElement(TemplateWrapper, { key: componentKey, templateFactory: factory, data: data, index: index, container: container, onRemoved: onRemoved, onRendered: onRendered });
        }
        return null;
    })));
};
